import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Mic, MicOff, Volume2, VolumeX, Square } from "lucide-react";
import axios from "axios";
import { API } from "../../lib/api";
import { parseEntrySource } from "../../lib/source";
import { useSpeaker, useAutoSendDictation, ttsSupported, sttSupported } from "./useSpeech";
import { TranscriptEmail } from "./TranscriptEmail";

const sid = () => (crypto?.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const renderText = (text) =>
  String(text || "").split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>;
    return part.replace(/\*+/g, "");
  });

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const sessionId = useRef(sid());
  const scrollRef = useRef(null);
  const idleTimer = useRef(null);
  const nudges = useRef(0);
  const [autoRead, setAutoRead] = useState(false);
  const autoReadRef = useRef(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const voiceModeRef = useRef(false);
  const awaitListenRef = useRef(false);
  const sendRef = useRef(null);
  const micRef = useRef(null);
  const setVoice = (v) => { voiceModeRef.current = v; setVoiceMode(v); };
  const listenAgain = () => { if (voiceModeRef.current) setTimeout(() => { if (voiceModeRef.current) micRef.current.start(""); }, 300); };
  const { speakingId, speak, stop } = useSpeaker({
    onFinished: () => { if (awaitListenRef.current) { awaitListenRef.current = false; listenAgain(); } },
  });
  const { listening, start: startMic, finish: stopMic, cancel: cancelMic } = useAutoSendDictation({
    onUpdate: (t) => setInput(t),
    onDone: (t) => sendRef.current && sendRef.current(t),
    onIdle: () => listenAgain(),
    onFail: (code) => { if (["not-allowed", "service-not-allowed", "audio-capture", "start-failed"].includes(code)) setVoice(false); else listenAgain(); },
  });
  micRef.current = { start: startMic };
  const toggleVoice = () => {
    if (voiceMode) { setVoice(false); awaitListenRef.current = false; if (listening) stopMic(); return; }
    setVoice(true);
    stop();
    startMic(input.trim());
  };
  const speakManual = (m) => {
    if (speakingId === m.id) { stop(); listenAgain(); return; }
    if (listening) { cancelMic(); awaitListenRef.current = voiceModeRef.current; }
    speak(m.id, m.text);
  };
  const afterReply = (msg) => {
    if (autoReadRef.current) { awaitListenRef.current = voiceModeRef.current; speak(msg.id, msg.text); }
    else listenAgain();
  };
  const toggleAutoRead = () => {
    const next = !autoRead;
    setAutoRead(next);
    autoReadRef.current = next;
    if (!next) stop();
  };

  const scrollToEnd = () => requestAnimationFrame(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  });

  const clearIdle = () => { if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; } };
  const scheduleIdle = () => {
    clearIdle();
    if (nudges.current >= 1) return;
    idleTimer.current = setTimeout(() => { nudges.current += 1; send("__inactive__", "hidden"); }, 75000);
  };

  const send = async (text, mode = "normal") => {
    if (busy) return;
    clearIdle();
    if (mode === "normal") setMessages((m) => [...m, { id: sid(), role: "user", text }]);
    if (mode !== "hidden") setInput("");
    setBusy(true);
    scrollToEnd();
    try {
      const { source } = parseEntrySource();
      const r = await axios.post(`${API}/chat/message`, {
        session_id: sessionId.current,
        message: mode === "opening" ? "" : text,
        source,
      }).then((x) => x.data);
      const msg = { id: sid(), role: "assistant", text: r.reply };
      setMessages((m) => [...m, msg]);
      afterReply(msg);
    } catch (e) {
      setMessages((m) => [...m, { id: sid(), role: "assistant", text: "Sorry — something went wrong. Please try again, or use the form on this page." }]);
      listenAgain();
    } finally {
      setBusy(false);
      scrollToEnd();
      scheduleIdle();
    }
  };

  useEffect(() => () => clearIdle(), []);
  sendRef.current = send;

  useEffect(() => {
    if (open && !started) {
      setStarted(true);
      send("", "opening");
    }
  }, [open, started]);

  useEffect(() => { scrollToEnd(); }, [messages, busy]);

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-launcher"
          aria-label="Chat with us"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-brand-black text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:bg-brand-gold transition-colors"
        >
          <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
          <span className="text-sm font-semibold">Chat with us</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-40 w-[min(92vw,380px)] h-[min(80vh,560px)] bg-white border border-border rounded-lg shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-brand-black text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div>
              <p className="font-serif text-base leading-tight">Family Court Guide</p>
              <p className="text-[11px] text-white/70">We can help you make an enquiry</p>
            </div>
            <div className="flex items-center gap-3">
              {ttsSupported && (
                <button onClick={toggleAutoRead} data-testid="chat-autoread-toggle" aria-pressed={autoRead}
                  aria-label={autoRead ? "Turn off auto-read replies" : "Turn on auto-read replies"}
                  title={autoRead ? "Auto-read replies: on" : "Auto-read replies: off"}
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border transition-colors ${autoRead ? "border-brand-gold text-brand-gold" : "border-white/30 text-white/80 hover:text-brand-gold"}`}>
                  {autoRead ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                  Auto-read {autoRead ? "on" : "off"}
                </button>
              )}
              <button onClick={() => { setVoice(false); awaitListenRef.current = false; stop(); cancelMic(); setOpen(false); }} data-testid="chat-close" aria-label="Close chat" className="text-white/80 hover:text-brand-gold">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-brand-grey/20">
            {messages.map((m, i) => (
              <div key={m.id} data-testid={`chat-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={[
                  "max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed",
                  m.role === "user" ? "bg-brand-black text-white rounded-br-sm" : "bg-white border border-border text-brand-black rounded-bl-sm",
                ].join(" ")}>
                  {renderText(m.text)}
                  {m.role === "assistant" && ttsSupported && (
                    <button type="button" onClick={() => speakManual(m)}
                      data-testid={`chat-speak-${i}`} aria-label={speakingId === m.id ? "Stop reading" : "Read aloud"}
                      className="mt-1 flex items-center gap-1 text-[11px] text-brand-darkgrey hover:text-brand-gold">
                      {speakingId === m.id ? <Square className="h-3 w-3" /> : <Volume2 className="h-3.5 w-3.5" />}
                      {speakingId === m.id ? "Stop" : "Listen"}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
                </div>
              </div>
            )}
          </div>

          {messages.some((m) => m.role === "user") && <TranscriptEmail sessionId={sessionId.current} />}
          {voiceMode && (
            <p className="px-3 pt-2 text-[11px] text-brand-darkgrey bg-white border-t border-border" data-testid="chat-voice-hint">
              Voice conversation on — {listening ? "listening, your message sends after a 3-second pause." : speakingId ? "reading the reply, then listening again." : "waiting for the reply."} Tap the mic to stop.
            </p>
          )}
          <form
            onSubmit={(e) => { e.preventDefault(); if (listening) { stopMic(); return; } const t = input.trim(); if (t) send(t); }}
            className="border-t border-border p-2.5 flex items-center gap-2 shrink-0 bg-white"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={listening ? "Listening… speak now" : voiceMode ? "Voice on — please wait…" : "Type or speak your message…"}
              data-testid="chat-input"
              disabled={busy}
              className="flex-1 text-sm px-3 py-2 border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-brand-gold"
            />
            {sttSupported && (
              <button type="button" onClick={toggleVoice} data-testid="chat-mic"
                aria-label={voiceMode ? "Turn off voice conversation" : "Start voice conversation"} aria-pressed={voiceMode}
                title={voiceMode ? "Turn off voice conversation" : "Start voice conversation"}
                className={`shrink-0 h-9 w-9 rounded-full inline-flex items-center justify-center border transition-colors ${listening ? "bg-brand-danger text-white border-brand-danger animate-pulse" : voiceMode ? "bg-brand-gold text-white border-brand-gold" : "border-border text-brand-black hover:border-brand-gold hover:text-brand-gold"}`}>
                {voiceMode ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            )}
            <button type="submit" data-testid="chat-send" disabled={busy || !input.trim()}
              aria-label="Send message"
              className="shrink-0 bg-brand-black text-white h-9 w-9 rounded-full inline-flex items-center justify-center hover:bg-brand-gold transition-colors disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
